import { useEffect, useState, useCallback, useRef } from 'react';
import { getFilteredWorkouts, Workout, WorkoutFilters } from '../api/client';
import { WorkoutCard } from '../components/WorkoutCard';
import { WorkoutCardSkeleton } from '../components/Skeleton';
import { ErrorState, EmptyState } from '../components/States';
import { haptic } from '../lib/telegram';
import './WorkoutsList.css';

interface WorkoutsListProps {
  userId: number;
  onBack: () => void;
  onOpenWorkout: (id: number) => void;
}

const PAGE_SIZE = 20;

export const WorkoutsList: React.FC<WorkoutsListProps> = ({ userId, onBack, onOpenWorkout }) => {
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState<WorkoutFilters>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const offsetRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    offsetRef.current = 0;
    try {
      const data = await getFilteredWorkouts(userId, PAGE_SIZE, 0, filters);
      setWorkouts(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err: any) {
      setError(err?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [userId, filters]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextOffset = offsetRef.current + PAGE_SIZE;
      const data = await getFilteredWorkouts(userId, PAGE_SIZE, nextOffset, filters);
      offsetRef.current = nextOffset;
      setWorkouts(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err: any) {
      setError(err?.message || 'Ошибка загрузки');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="workouts-list-page">
      <header className="page-header">
        <button className="back-button" onClick={() => { haptic.medium(); onBack(); }} aria-label="Назад">
          ←
        </button>
        <h1>Все тренировки</h1>
        <button className={`filter-toggle ${filtersOpen ? 'active' : ''}`} onClick={() => setFiltersOpen(value => !value)}>
          Фильтры
        </button>
      </header>

      {filtersOpen && (
        <div className="workout-filters">
          <label>
            <span>Тип</span>
            <select value={filters.activityType || ''} onChange={event => setFilters(current => ({ ...current, activityType: event.target.value || undefined }))}>
              <option value="">Все</option>
              <option value="улиц">На улице</option>
              <option value="помещ">В помещении</option>
            </select>
          </label>
          <label>
            <span>Дистанция</span>
            <select
              value={filters.distanceMin === 5 && filters.distanceMax === 10 ? '5-10' : filters.distanceMax === 5 ? '0-5' : filters.distanceMin === 10 ? '10+' : ''}
              onChange={event => {
                const value = event.target.value;
                setFilters(current => ({
                  ...current,
                  distanceMin: value === '5-10' ? 5 : value === '10+' ? 10 : undefined,
                  distanceMax: value === '0-5' ? 5 : value === '5-10' ? 10 : undefined
                }));
              }}
            >
              <option value="">Любая</option>
              <option value="0-5">До 5 км</option>
              <option value="5-10">5–10 км</option>
              <option value="10+">От 10 км</option>
            </select>
          </label>
          <label>
            <span>Интенсивность</span>
            <select value={filters.intensity || ''} onChange={event => setFilters(current => ({ ...current, intensity: event.target.value || undefined }))}>
              <option value="">Любая</option>
              <option value="низкая">Низкая</option>
              <option value="средняя">Средняя</option>
              <option value="высокая">Высокая</option>
            </select>
          </label>
          <button onClick={() => setFilters({})}>Сбросить</button>
        </div>
      )}

      {loading ? (
        <div className="workouts-list">
          {Array.from({ length: 5 }).map((_, i) => <WorkoutCardSkeleton key={i} />)}
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : workouts.length === 0 ? (
        <EmptyState
          icon="—"
          title="Тренировок пока нет"
          hint="Отправьте скриншот тренировки боту, чтобы он появился здесь"
        />
      ) : (
        <>
          <p className="list-count">{workouts.length} тренировок</p>
          <div className="workouts-list">
            {workouts.map(w => (
              <WorkoutCard
                key={w.id}
                date={w.date}
                exercises={w.exercises}
                aiAnalysis={w.ai_analysis}
                runningStats={w.running_stats}
                score={w.score}
                onClick={() => { haptic.light(); onOpenWorkout(w.id); }}
              />
            ))}
          </div>
          {hasMore && (
            <button
              className="load-more-button"
              onClick={() => { haptic.light(); loadMore(); }}
              disabled={loadingMore}
            >
              {loadingMore ? 'Загрузка…' : 'Загрузить ещё'}
            </button>
          )}
        </>
      )}
    </div>
  );
};
